import React from 'react';
import EventCard from './EventCard';
import PickupCard from './PickupCard';
import { SESSION_STATUS, SHOW_CAREGIVER_SESSION_HINTS } from '../config/appConfig';

// ─── DAY CARD ─────────────────────────────────────────────────────────────────

// Person role labels for the column headers
const PERSON_ROLES = {
  'Adreck': 'PRIMARY',
  'Katy':   'PRIMARY',
  'Cal':    'KIDS',
  'Anylah': 'KIDS',
  'Lee':    'KIDS',
  'Sam':    'KIDS',
};

// Exported so other Review & Assign views (e.g. the workload-balance rail
// in LogisticsModule) color the same person consistently.
export const PERSON_COLORS = {
  'Adreck': '#3A5EA8',
  'Katy':   '#C2185B',
  'Cal':    '#00897B',
  'Anylah': '#F57C00',
  'Lee':    '#6A1B9A',
  'Sam':    '#5D4037',
};

export default function DayCard({ day, pendingAssignments, onAssign, onAssignPickup, onCancel, onToggleReminder, onIgnore, onAddEvent, isExpanded, onToggle, allSitters = [] }) {
  const alertLevel = day.alertLevel || 'assigned';
  const LEVEL_COLORS = {
    conflict:   '#9C4040',   // deep earthy red
    unassigned: '#A87830',   // warm amber-bark — slightly richer
    assigned:   '#8D9986',   // sage — subtle, not demanding attention
  };
  const borderColor = LEVEL_COLORS[alertLevel] || LEVEL_COLORS.assigned;
  const hasDeclinedSession = (day.sitterSessions || []).some(s => s.status === SESSION_STATUS.DECLINED);

  const carePartners = day.carePartnerRecords || day.carePartners || [];
  const pickupCaregivers = allSitters.filter(s => s.pickupEligible && s.active !== false);

  // Column grouping/matching stays keyed on the full name (calendarAssignments
  // and event._personName are always full names) — this only resolves what
  // gets RENDERED, preferring an explicit short name, falling back to first name.
  function displayNameFor(fullName) {
    const cp = carePartners.find(c => c.name === fullName);
    if (!cp) return fullName;
    return cp.shortName || cp.name?.split(' ')[0] || fullName;
  }

  // Merge pending pickup changes on top of the fetched pickups for this day
  const effectivePickups = (day.pickups || []).map(p => {
    const pend = pendingAssignments[`pickup_${p.key}`];
    if (!pend) return p;
    if (pend.revert)             return { ...p, adultId: p.defaultAdultId, skipped: false, source: 'default', _pending: true };
    if (pend.adultId === 'skip') return { ...p, adultId: null, skipped: true, source: 'override', _pending: true };
    return { ...p, adultId: pend.adultId, skipped: false, source: 'override', _pending: true };
  });

  const unsetOrSkipped = effectivePickups.filter(p => (!p.adultId && !p.skipped) || p.skipped);
  const assignedPickups = effectivePickups.filter(p => p.adultId && !p.skipped);

  function resolvePickupPersonName(adultId) {
    if (adultId.startsWith('cg:')) {
      const s = allSitters.find(x => x.id === adultId.slice(3));
      return s ? s.firstName : null;
    }
    const cp = carePartners.find(x => x.identifier === adultId);
    return cp ? cp.name : null;
  }

  // Group events by person (owner name derived from _personName field or calendar assignment)
  const grouped = {};
  const pickupsByPerson = {};
  const allPersons = [...carePartners.map(cp => cp.name), ...(day.children || [])];

  for (const person of allPersons) {
    grouped[person] = [];
    pickupsByPerson[person] = [];
  }
  grouped['Other'] = [];

  for (const event of (day.events || [])) {
    const owner = event._personName || 'Other';
    if (!grouped[owner]) grouped[owner] = [];
    grouped[owner].push(event);
  }

  for (const p of assignedPickups) {
    const name = resolvePickupPersonName(p.adultId);
    if (!name) continue;
    if (!pickupsByPerson[name]) pickupsByPerson[name] = [];
    pickupsByPerson[name].push(p);
  }

  // Render a column for anyone with an event OR an assigned pickup
  const columnNames = new Set([
    ...Object.entries(grouped).filter(([, evts]) => evts.length > 0).map(([name]) => name),
    ...Object.entries(pickupsByPerson).filter(([, ps]) => ps.length > 0).map(([name]) => name),
  ]);
  const columns = [...columnNames].map(name => [name, grouped[name] || []]);

  // Event count + cal count for header summary
  const totalEvents = day.events?.length || 0;
  const calCount = new Set((day.events || []).map(e => e._calendarId)).size;
  const unsetPickupCount = unsetOrSkipped.filter(p => !p.adultId && !p.skipped).length;

  return (
    <div className="day-card" style={{ borderLeftColor: borderColor }}>
      {/* Day header — always visible */}
      <button className="day-card__header" onClick={onToggle}>
        <div className="day-card__date">
          <span className="day-card__dow">{day.dayOfWeek}</span>
          <span className="day-card__fulldate">{day.fullDate}</span>
        </div>
        <div className="day-card__indicators">
          {totalEvents > 0 && (
            <span className="day-card__event-count">
              {totalEvents} event{totalEvents !== 1 ? 's' : ''}
              {calCount > 0 ? ` · ${calCount} cal${calCount !== 1 ? 's' : ''}` : ''}
            </span>
          )}
          {/* Status indicator based on reclassified alertLevel */}
          {alertLevel === 'conflict' && (
            <span className="day-card__status-pill day-card__status-pill--conflict">conflict</span>
          )}
          {alertLevel === 'unassigned' && (
            <span className="day-card__status-pill day-card__status-pill--unassigned">unassigned</span>
          )}
          {alertLevel === 'assigned' && (
            <span className="day-card__status-pill day-card__status-pill--assigned">✓</span>
          )}
          {unsetPickupCount > 0 && (
            <span className="day-card__status-pill day-card__status-pill--unassigned" title="School pickup has no adult assigned">
              🚗 {unsetPickupCount} pickup{unsetPickupCount !== 1 ? 's' : ''} unset
            </span>
          )}
          {/* Declined stays visible (actionable) even while the plain
              "scheduled" badge is suppressed — see SHOW_CAREGIVER_SESSION_HINTS */}
          {hasDeclinedSession && (
            <span className="badge badge--red" title="A caregiver declined — needs a new one">⚠️ Declined</span>
          )}
          {SHOW_CAREGIVER_SESSION_HINTS && day.hasSitterPending && !hasDeclinedSession && (
            <span className="sitter-badge" title="Caregiver scheduled">🌱</span>
          )}
        </div>
        {onAddEvent && (
          <button
            className="day-card__add"
            onClick={e => { e.stopPropagation(); onAddEvent(day.isoDate); }}
            title="Add event on this day"
          >🗓️＋</button>
        )}
        <span className="day-card__expand-icon">{isExpanded ? '−' : '+'}</span>
      </button>

      {/* Expanded: context summary + pickups + person-grouped columns */}
      {isExpanded && (
        <div className="day-card__body">
          {/* Context summary bar */}
          {day.contextSummary && (
            <div className="day-context-bar">
              <span className="day-context-bar__icon">💬</span>
              <span className="day-context-bar__text">{day.contextSummary}</span>
            </div>
          )}

          {/* Pickup action strip — unset or skipped pickups, assignable right here */}
          {unsetOrSkipped.length > 0 && (
            <div className="pickup-action-strip">
              {unsetOrSkipped.map(p => (
                <PickupCard
                  key={p.key}
                  pickup={p}
                  isPending={p._pending}
                  onAssign={adultId => onAssignPickup(p, adultId)}
                  carePartners={carePartners}
                  pickupCaregivers={pickupCaregivers}
                  sitters={allSitters}
                />
              ))}
              <p className="pickup-action-strip__note">Weekly defaults: ↻ Defaults</p>
            </div>
          )}

          {totalEvents === 0 && assignedPickups.length === 0 ? (
            <p className="day-card__empty">No events this day</p>
          ) : (
            <div className="day-columns">
              {columns.map(([personName, events]) => {
                const displayName = displayNameFor(personName);
                return (
                <div key={personName} className="day-column">
                  {/* Column header */}
                  <div className="day-column__header">
                    <span
                      className="day-column__name"
                      style={{ color: PERSON_COLORS[displayName] || 'var(--navy)' }}
                    >{displayName.toUpperCase()}</span>
                    <span className="day-column__role">
                      {PERSON_ROLES[displayName] || 'FAMILY'}
                    </span>
                  </div>

                  {/* Assigned pickups for this person */}
                  {(pickupsByPerson[personName] || []).map(p => (
                    <PickupCard
                      key={p.key}
                      pickup={p}
                      isPending={p._pending}
                      onAssign={adultId => onAssignPickup(p, adultId)}
                      carePartners={carePartners}
                      pickupCaregivers={pickupCaregivers}
                      sitters={allSitters}
                    />
                  ))}

                  {/* Events in this column */}
                  {events
                    .sort((a, b) => (a.startTime || 'ZZ').localeCompare(b.startTime || 'ZZ'))
                    .map(event => (
                      <EventCard
                        key={event.id}
                        event={event}
                        pending={pendingAssignments[event.id]}
                        onAssign={(assignee, scope) => onAssign(event.id, assignee, event.fohMeta, scope)}
                        onToggleReminder={(scope) => onToggleReminder(event.id, scope)}
                        onIgnore={(scope) => onIgnore(event.id, event._raw?.recurringEventId, scope)}
                        onCancel={(reason) => onCancel(event.id, reason)}
                        carePartners={day.carePartners || []}
                        familySitters={day.familySitters || []}
                        sitterSessions={day.sitterSessions || []}
                        allSitters={allSitters}
                      />
                    ))
                  }
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
